// Плашка после вставки: что вставлено и что убрано + переиграть вставку в другом режиме одним щелчком
// («Только текст», «Как в исходнике», ссылки: текстом / гиперссылками / в список источников) или отменить.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { createRoot } from 'react-dom/client';
import { ctx } from '../../core/ctx';
import { Btn } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { progress, runPaste, summary, undoSession, type PasteOpts, type Session } from './apply';
import { setPref } from './prefs';

// ------------------------------------------------------------------------------------------------ состояние
let current: Session | null = null;
let busy = false;
let busyText = '';
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((f) => f());
const snap = () => current;
const snapBusy = () => busy;
const sub = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };

export function showBar(s: Session | null) {
  current = s && (s.blocksCount > 0 || s.stats) ? s : null;
  emit();
}
export function hideBar() { current = null; emit(); }
export function setBusy(b: boolean) { busy = b; busyText = ''; emit(); }
progress.set = (t: string) => { busyText = t; emit(); };
const snapText = () => busyText;
export function currentSession() { return current; }

/** Переиграть последнюю вставку с другими параметрами. */
export async function repaste(opts: Partial<PasteOpts>, remember = false) {
  const s = current;
  if (!s || busy) return;
  if (!undoSession(ctx, s)) {
    ctx.toast('После вставки документ уже менялся - отмените вставку вручную (Ctrl+Z) и вставьте снова', 'warn', 6000);
    hideBar();
    return;
  }
  const next = { ...s.opts, ...opts };
  if (remember && opts.links) setPref('links', opts.links);
  setBusy(true);
  try {
    showBar(await runPaste(ctx, s.input, next, s.cache));
  } catch (e: any) {
    ctx.toast('Вставка: ' + (e?.message || e), 'error', 6000);
    hideBar();
  } finally {
    setBusy(false);
  }
}

// ------------------------------------------------------------------------------------------------ вид
function PasteBar() {
  const s = useSyncExternalStore(sub, snap);
  const isBusy = useSyncExternalStore(sub, snapBusy);
  const text = useSyncExternalStore(sub, snapText);
  const [hover, setHover] = useState(false);
  useEffect(() => {
    if (!s || hover) return;
    // плашка держится до следующей правки документа (после неё «Отменить вставку» уже не одним действием) или 5 мин
    const t = window.setTimeout(hideBar, 5 * 60000);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') hideBar(); };
    window.addEventListener('keydown', onKey);
    const ed = ctx.editor;
    const onUpd = () => { if (s.docAfter && ed && !ed.state.doc.eq(s.docAfter)) hideBar(); };
    ed?.on('update', onUpd);
    return () => { window.clearTimeout(t); window.removeEventListener('keydown', onKey); ed?.off('update', onUpd); };
  }, [s, hover]);
  if (!s) return isBusy ? <div className="pst-bar pst-busy" data-testid="paste-busy"><Icon name="spinner" className="spin" /> Вставка{text ? ': ' + text : '…'}</div> : null;
  const { title, parts, cleaned } = summary(s);
  const mode = s.opts.mode;
  const rich = !s.fixed && (s.app !== 'text' || mode === 'text');
  return (
    <div className="pst-bar" data-testid="paste-bar" role="status" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="pst-main">
        <Icon name="paste" size={18} />
        <div className="pst-text">
          <div className="pst-title" data-testid="paste-bar-title">{title}{parts.length ? ': ' : ''}{parts.join(', ')}</div>
          {cleaned.length > 0 && <div className="pst-sub">Убрано: {cleaned.join('; ')}</div>}
        </div>
        <button type="button" className="pst-x" aria-label="Закрыть" data-testid="paste-bar-close" onClick={hideBar}><Icon name="close" size={14} /></button>
      </div>
      <div className="pst-actions">
        {mode !== 'structure' && rich && <Btn icon="sparkles" label="Распознать структуру СТО" disabled={isBusy} testid="paste-bar-structure"
          title="Заголовки, списки, подписи, формулы, ссылки - как в исходнике, но по правилам СТО" onClick={() => repaste({ mode: 'structure' })} />}
        {mode === 'structure' && rich && s.app !== 'text' && s.app !== 'markdown' && <Btn icon="eraser" label="Отменить очистку" disabled={isBusy} testid="paste-bar-basic"
          title="Без распознавания подписей, номеров и ссылок: как в исходном документе (только без шрифтов и цветов)" onClick={() => repaste({ mode: 'basic' })} />}
        {mode !== 'text' && !s.fixed && <Btn icon="text" label="Только текст" disabled={isBusy} testid="paste-bar-text"
          title="Только абзацы текста, без таблиц, рисунков и оформления" onClick={() => repaste({ mode: 'text' })} />}
        {s.hasLinks && !s.fixed && mode !== 'text' && (
          <span className="pst-links" role="group" aria-label="Гиперссылки">
            <span className="pst-lbl">Ссылки:</span>
            {([['text', 'текстом', 'Оставить только текст ссылок (в отчёте по СТО гиперссылки не нужны)'],
              ['keep', 'гиперссылками', 'Сохранить гиперссылки'],
              ['sources', 'в источники', 'Адреса ссылок - в список использованных источников, в тексте - [N]']] as const).map(([k, l, t]) => (
              <button key={k} type="button" className={'pst-seg' + (s.opts.links === k ? ' is-on' : '')} data-tip={t} disabled={isBusy}
                data-testid={'paste-bar-links-' + k} onClick={() => s.opts.links !== k && repaste({ links: k }, true)}>{l}</button>
            ))}
          </span>
        )}
        <span className="pst-spacer" />
        <Btn icon="undo" label="Отменить вставку" disabled={isBusy} testid="paste-bar-undo" onClick={() => {
          if (!undoSession(ctx, s)) ctx.toast('После вставки документ уже менялся - используйте Ctrl+Z', 'warn', 5000);
          hideBar();
        }} />
      </div>
    </div>
  );
}

let mounted = false;
export function mountBar() {
  if (mounted) return;
  mounted = true;
  const host = document.createElement('div');
  host.id = 'sto-paste-bar';
  document.body.appendChild(host);
  createRoot(host).render(<PasteBar />);
}
